import React, { useMemo, useState } from "react";

import Modal from "../../components/Modal";
import { CREATE_LOT_SETTLEMENT_URL, postData } from "../../utils/api";

const METALS = [
  { key: "pt", label: "Platinum", defaultReturnPct: "97.50" },
  { key: "pd", label: "Palladium", defaultReturnPct: "97.50" },
  { key: "rh", label: "Rhodium", defaultReturnPct: "91.50" },
];

let ROW_SEQ = 1;
function nextRowId() {
  ROW_SEQ += 1;
  return `row-${ROW_SEQ}-${Date.now()}`;
}

function emptyRow(metalKey, shipmentId = "", returnPct = "97.50") {
  return {
    id: nextRowId(),
    metal: metalKey,
    shipmentId,
    containedOz: "",
    contractReturnPct: returnPct,
    metalPrice: "",
  };
}

const OEM_OPTIONS = ["BASF", "SW"];

function emptyForm() {
  return {
    oem: "",
    abcatLotId: "",
    oemLotId: "",
    smcSettlementNo: "",
    finalPaymentDate: "",
    dateReceived: "",
    valueDate: "",
    lines: {
      pt: [emptyRow("pt", "", "97.50")],
      pd: [emptyRow("pd", "", "97.50")],
      rh: [emptyRow("rh", "", "91.50")],
    },
    preliminaryFinanceCharge: "",
    preliminaryPayments: "",
    treatment: "",
    financeChargePct: "",
    financeCharge: "",
  };
}

function toNum(v) {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function calcReturnable(containedOz, returnPct) {
  const oz = toNum(containedOz);
  const pct = toNum(returnPct);
  if (oz == null || pct == null) return null;
  return oz * (pct / 100);
}

function calcValue(returnableOz, metalPrice) {
  const oz = returnableOz;
  const price = toNum(metalPrice);
  if (oz == null || price == null) return null;
  return oz * price;
}

function formatOzInput(n) {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toFixed(5);
}

function formatMoney(n, { signed = false } = {}) {
  if (n == null || Number.isNaN(n)) return "—";
  const abs = Math.abs(n).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (signed && n < 0) return `−${abs}`;
  if (signed && n > 0) return abs;
  return abs;
}

const CreateSettlementModal = ({ onClose, onSuccess, onError }) => {
  const [form, setForm] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateHeader = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "abcatLotId") {
        next.lines = {
          pt: prev.lines.pt.map((r) =>
            !r.shipmentId || r.shipmentId === prev.abcatLotId
              ? { ...r, shipmentId: value }
              : r
          ),
          pd: prev.lines.pd.map((r) =>
            !r.shipmentId || r.shipmentId === prev.abcatLotId
              ? { ...r, shipmentId: value }
              : r
          ),
          rh: prev.lines.rh.map((r) =>
            !r.shipmentId || r.shipmentId === prev.abcatLotId
              ? { ...r, shipmentId: value }
              : r
          ),
        };
      }
      return next;
    });
  };

  const updateLine = (metalKey, rowId, field, value) => {
    setForm((prev) => ({
      ...prev,
      lines: {
        ...prev.lines,
        [metalKey]: prev.lines[metalKey].map((r) =>
          r.id === rowId ? { ...r, [field]: value } : r
        ),
      },
    }));
  };

  const addLine = (metalKey) => {
    const meta = METALS.find((m) => m.key === metalKey);
    setForm((prev) => ({
      ...prev,
      lines: {
        ...prev.lines,
        [metalKey]: [
          ...prev.lines[metalKey],
          emptyRow(metalKey, prev.abcatLotId, meta?.defaultReturnPct || "97.50"),
        ],
      },
    }));
  };

  const removeLine = (metalKey, rowId) => {
    setForm((prev) => {
      const rows = prev.lines[metalKey];
      if (rows.length <= 1) {
        return {
          ...prev,
          lines: {
            ...prev.lines,
            [metalKey]: [{ ...emptyRow(metalKey, prev.abcatLotId, rows[0]?.contractReturnPct) }],
          },
        };
      }
      return {
        ...prev,
        lines: {
          ...prev.lines,
          [metalKey]: rows.filter((r) => r.id !== rowId),
        },
      };
    });
  };

  const metalTotals = useMemo(() => {
    const result = {};
    METALS.forEach(({ key }) => {
      let contained = 0;
      let returnable = 0;
      let value = 0;
      let hasContained = false;
      let hasReturnable = false;
      let hasValue = false;
      form.lines[key].forEach((row) => {
        const c = toNum(row.containedOz);
        const r = calcReturnable(row.containedOz, row.contractReturnPct);
        const v = calcValue(r, row.metalPrice);
        if (c != null) {
          contained += c;
          hasContained = true;
        }
        if (r != null) {
          returnable += r;
          hasReturnable = true;
        }
        if (v != null) {
          value += v;
          hasValue = true;
        }
      });
      result[key] = {
        contained: hasContained ? contained : null,
        returnable: hasReturnable ? returnable : null,
        value: hasValue ? value : null,
      };
    });
    return result;
  }, [form.lines]);

  const finalLotValue = useMemo(() => {
    let sum = 0;
    let any = false;
    METALS.forEach(({ key }) => {
      if (metalTotals[key].value != null) {
        sum += metalTotals[key].value;
        any = true;
      }
    });
    return any ? sum : null;
  }, [metalTotals]);

  const totalFinalPayment = useMemo(() => {
    if (finalLotValue == null) return null;
    const prelimFin = toNum(form.preliminaryFinanceCharge) || 0;
    const prelimPay = toNum(form.preliminaryPayments) || 0;
    const treatment = toNum(form.treatment) || 0;
    const finCharge = toNum(form.financeCharge) || 0;
    return finalLotValue - Math.abs(prelimFin) - Math.abs(prelimPay) - Math.abs(treatment) - Math.abs(finCharge);
  }, [finalLotValue, form.preliminaryFinanceCharge, form.preliminaryPayments, form.treatment, form.financeCharge]);

  const buildPayload = () => {
    const lines = [];
    METALS.forEach(({ key }) => {
      form.lines[key].forEach((row) => {
        const containedOz = toNum(row.containedOz);
        const contractReturnPct = toNum(row.contractReturnPct);
        const metalPrice = toNum(row.metalPrice);
        const returnableOz = calcReturnable(row.containedOz, row.contractReturnPct);
        const value = calcValue(returnableOz, row.metalPrice);
        const hasData =
          containedOz != null ||
          metalPrice != null ||
          (row.shipmentId && row.shipmentId.trim());
        if (!hasData) return;
        lines.push({
          metal: key,
          shipmentId: (row.shipmentId || "").trim() || null,
          containedOz: containedOz ?? 0,
          contractReturnPct: contractReturnPct ?? 0,
          returnableOz: returnableOz ?? 0,
          metalPrice: metalPrice ?? 0,
          value: value ?? 0,
        });
      });
    });

    return {
      oem: form.oem,
      abcatLotId: (form.abcatLotId || "").trim(),
      oemLotId: (form.oemLotId || "").trim() || null,
      smcSettlementNo: (form.smcSettlementNo || "").trim() || null,
      finalPaymentDate: form.finalPaymentDate || null,
      dateReceived: form.dateReceived || null,
      valueDate: form.valueDate || null,
      lines,
      preliminaryFinanceCharge: Math.abs(toNum(form.preliminaryFinanceCharge) || 0),
      preliminaryPayments: Math.abs(toNum(form.preliminaryPayments) || 0),
      treatment: Math.abs(toNum(form.treatment) || 0),
      financeChargePct: toNum(form.financeChargePct) || 0,
      financeCharge: Math.abs(toNum(form.financeCharge) || 0),
      finalLotValue: finalLotValue ?? 0,
      totalFinalPayment: totalFinalPayment ?? 0,
    };
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!form.oem) {
      if (onError) onError("Select an OEM");
      return;
    }
    if (!(form.abcatLotId || "").trim()) {
      if (onError) onError("Enter ABCAT LOT ID");
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = buildPayload();
      const data = await postData(CREATE_LOT_SETTLEMENT_URL, payload);
      const result = data?.data || data || {};
      if (onSuccess) {
        onSuccess(result.message || "Lot settlement created", result.lotSettlementId);
      }
      onClose();
    } catch (e) {
      const message =
        (e && e.response && e.response.data && e.response.data.error) ||
        (e && e.error) ||
        "Failed to create lot settlement";
      if (onError) onError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Create Settlement" maxWidth="1120px">
      <div className="lot-settle-form">
        <div className="lot-settle-header-grid">
          <label className="lot-settle-field">
            <span>OEM</span>
            <select
              value={form.oem}
              onChange={(e) => updateHeader("oem", e.target.value)}
            >
              <option value="">Select OEM</option>
              {OEM_OPTIONS.map((oem) => (
                <option key={oem} value={oem}>
                  {oem}
                </option>
              ))}
            </select>
          </label>
          <label className="lot-settle-field">
            <span>ABCAT LOT ID</span>
            <input
              type="text"
              value={form.abcatLotId}
              onChange={(e) => updateHeader("abcatLotId", e.target.value)}
              placeholder="e.g. STW406"
            />
          </label>
          <label className="lot-settle-field">
            <span>OEM LOT ID</span>
            <input
              type="text"
              value={form.oemLotId}
              onChange={(e) => updateHeader("oemLotId", e.target.value)}
              placeholder="e.g. AV00145"
            />
          </label>
          <label className="lot-settle-field">
            <span>SMC Settlement No.</span>
            <input
              type="text"
              value={form.smcSettlementNo}
              onChange={(e) => updateHeader("smcSettlementNo", e.target.value)}
              placeholder="e.g. SET037174"
            />
          </label>
          <label className="lot-settle-field">
            <span>Final Payment Date</span>
            <input
              type="date"
              value={form.finalPaymentDate}
              onChange={(e) => updateHeader("finalPaymentDate", e.target.value)}
            />
          </label>
          <label className="lot-settle-field">
            <span>Date Received</span>
            <input
              type="date"
              value={form.dateReceived}
              onChange={(e) => updateHeader("dateReceived", e.target.value)}
            />
          </label>
          <label className="lot-settle-field">
            <span>Value Date</span>
            <input
              type="date"
              value={form.valueDate}
              onChange={(e) => updateHeader("valueDate", e.target.value)}
            />
          </label>
        </div>

        <div className="lot-settle-table-wrap">
          <table className="lot-settle-table">
            <thead>
              <tr>
                <th>Metal</th>
                <th>Shipment ID</th>
                <th>Contained Ounces</th>
                <th>Contract Return %</th>
                <th>Returnable Ounces</th>
                <th>Metal Price</th>
                <th>Value ($)</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {METALS.map(({ key, label }) => {
                const rows = form.lines[key];
                const sub = metalTotals[key];
                return (
                  <React.Fragment key={key}>
                    {rows.map((row, idx) => {
                      const returnable = calcReturnable(row.containedOz, row.contractReturnPct);
                      const value = calcValue(returnable, row.metalPrice);
                      return (
                        <tr key={row.id} className="lot-settle-line">
                          <td className="lot-settle-metal-cell">
                            {idx === 0 ? (
                              <div className="lot-settle-metal-label-row">
                                <span className="lot-settle-metal-label">{label}</span>
                                <button
                                  type="button"
                                  className="lot-settle-add-row"
                                  onClick={() => addLine(key)}
                                  title={`Add ${label} row`}
                                  aria-label={`Add ${label} row`}
                                >
                                  +
                                </button>
                              </div>
                            ) : (
                              <span className="lot-settle-metal-muted">{label}</span>
                            )}
                          </td>
                          <td>
                            <input
                              type="text"
                              value={row.shipmentId}
                              onChange={(e) => updateLine(key, row.id, "shipmentId", e.target.value)}
                              placeholder="Shipment ID"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              step="any"
                              value={row.containedOz}
                              onChange={(e) => updateLine(key, row.id, "containedOz", e.target.value)}
                              placeholder="0.00000"
                            />
                          </td>
                          <td>
                            <div className="lot-settle-pct-wrap">
                              <input
                                type="number"
                                step="any"
                                value={row.contractReturnPct}
                                onChange={(e) =>
                                  updateLine(key, row.id, "contractReturnPct", e.target.value)
                                }
                                placeholder="97.50"
                              />
                              <span>%</span>
                            </div>
                          </td>
                          <td className="lot-settle-calc">{formatOzInput(returnable)}</td>
                          <td>
                            <div className="lot-settle-money-wrap">
                              <span>$</span>
                              <input
                                type="number"
                                step="any"
                                value={row.metalPrice}
                                onChange={(e) => updateLine(key, row.id, "metalPrice", e.target.value)}
                                placeholder="0.00"
                              />
                            </div>
                          </td>
                          <td className="lot-settle-calc lot-settle-value">{formatMoney(value)}</td>
                          <td>
                            <button
                              type="button"
                              className="lot-settle-remove-row"
                              onClick={() => removeLine(key, row.id)}
                              title="Remove row"
                              aria-label="Remove row"
                            >
                              ×
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="lot-settle-subtotal">
                      <td colSpan={2}>{label} subtotal</td>
                      <td>{formatOzInput(sub.contained)}</td>
                      <td />
                      <td>{formatOzInput(sub.returnable)}</td>
                      <td />
                      <td className="lot-settle-value">{formatMoney(sub.value)}</td>
                      <td />
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="lot-settle-summary">
          <div className="lot-settle-summary-card">
            <div className="lot-settle-summary-row">
              <span>Final Lot Value</span>
              <strong>{formatMoney(finalLotValue)}</strong>
            </div>
            <label className="lot-settle-summary-row">
              <span>Preliminary Finance Charge</span>
              <div className="lot-settle-money-wrap lot-settle-money-wrap--neg">
                <span>−$</span>
                <input
                  type="number"
                  step="any"
                  value={form.preliminaryFinanceCharge}
                  onChange={(e) => updateHeader("preliminaryFinanceCharge", e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </label>
            <label className="lot-settle-summary-row">
              <span>Preliminary Payments</span>
              <div className="lot-settle-money-wrap lot-settle-money-wrap--neg">
                <span>−$</span>
                <input
                  type="number"
                  step="any"
                  value={form.preliminaryPayments}
                  onChange={(e) => updateHeader("preliminaryPayments", e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </label>
            <label className="lot-settle-summary-row">
              <span>Treatment</span>
              <div className="lot-settle-money-wrap lot-settle-money-wrap--neg">
                <span>−$</span>
                <input
                  type="number"
                  step="any"
                  value={form.treatment}
                  onChange={(e) => updateHeader("treatment", e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </label>
            <div className="lot-settle-summary-row">
              <span>Finance Charge</span>
              <div className="lot-settle-finance-wrap">
                <div className="lot-settle-pct-wrap">
                  <input
                    type="number"
                    step="any"
                    value={form.financeChargePct}
                    onChange={(e) => updateHeader("financeChargePct", e.target.value)}
                    placeholder="0.00"
                    aria-label="Finance charge percent"
                  />
                  <span>%</span>
                </div>
                <div className="lot-settle-money-wrap lot-settle-money-wrap--neg">
                  <span>−$</span>
                  <input
                    type="number"
                    step="any"
                    value={form.financeCharge}
                    onChange={(e) => updateHeader("financeCharge", e.target.value)}
                    placeholder="0.00"
                    aria-label="Finance charge amount"
                  />
                </div>
              </div>
            </div>
            <div className="lot-settle-summary-row lot-settle-summary-row--total">
              <span>Total Final Payment</span>
              <strong>{formatMoney(totalFinalPayment)}</strong>
            </div>
          </div>
        </div>

        <div className="lot-hedge-create-modal-footer">
          <button
            type="button"
            className="lot-hedge-link-btn"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="lot-hedge-primary-btn"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Creating…" : "Create Settlement"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CreateSettlementModal;
